import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Building2,
    Edit,
    Mail,
    MapPin,
    Phone,
    Plus,
    RefreshCw,
    Search,
    ShieldCheck,
    Trash2,
    Users
} from 'lucide-react';
import Sidebar from '../../../../components/Sidebar/sidebar';
import Header from '../../../../components/Header';
import Pagination from '../../../../components/Pagination/Pagination';
import { getUserPrivileges, getUserRole, isAuthenticated } from '../../../../utils/auth';
import api from '../../../../utils/api';
import './insurerBranchMaster.css';
import { API_BASE_URL } from '../../../../utils/apiBase';
import { PRODUCT_MASTER_PRIVILEGES } from '../../../../utils/productMasterPrivileges';
import { toast } from '../../../../components/notifications/notificationService';

const initialFormData = {
    insurerId: '',
    branchName: '',
    contactPersonName: '',
    contactNumber: '',
    email: '',
    address: '',
    pinCode: '',
    city: '',
    state: ''
};

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const pinCodeRegex = /^\d{6}$/;
const contactNumberRegex = /^[0-9+()\-\s]{6,15}$/;

const InsurerBranchMaster = () => {
    const navigate = useNavigate();
    const privileges = getUserPrivileges();
    const isAdmin = getUserRole() === 'admin';
    const branchPrivileges = PRODUCT_MASTER_PRIVILEGES.branch;
    const canAccessPage = isAdmin || branchPrivileges.access.some((key) => privileges.includes(key));
    const canAddBranch = isAdmin || privileges.includes(branchPrivileges.add);
    const canEditBranch = isAdmin || privileges.includes(branchPrivileges.edit);
    const canDeleteBranch = isAdmin || privileges.includes(branchPrivileges.delete);

    const [collapsed, setCollapsed] = useState(false);
    const [showPopup, setShowPopup] = useState(false);
    const [branches, setBranches] = useState([]);
    const [insurers, setInsurers] = useState([]);
    const [formData, setFormData] = useState(initialFormData);
    const [formErrors, setFormErrors] = useState({});
    const [editId, setEditId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [pinLookupLoading, setPinLookupLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [branchToDelete, setBranchToDelete] = useState(null);
    const [filters, setFilters] = useState({
        search: '',
        insurerId: '',
        city: '',
        state: ''
    });

    useEffect(() => {
        if (!isAuthenticated()) {
            navigate('/');
            return;
        }

        if (!canAccessPage) {
            navigate('/unauthorized', { replace: true });
        }
    }, [canAccessPage, navigate]);

    useEffect(() => {
        const fetchInitialData = async () => {
            setLoading(true);
            try {
                const branchResponse = await api.get(`${API_BASE_URL}/api/insurer-branches`);
                setBranches(branchResponse.data || []);
            } catch (error) {
                showNotification('Error fetching insurer branches.', 'error');
            }

            try {
                const insurerResponse = await api.get(`${API_BASE_URL}/api/insurer-branches/insurers/list`);
                setInsurers(insurerResponse.data || []);
            } catch (error) {
                showNotification('Error fetching insurers for dropdown.', 'error');
            } finally {
                setLoading(false);
            }
        };

        fetchInitialData();
    }, []);

    const showNotification = (message, type) => {
        (toast[type] || toast.info)(message);
    };

    const resetForm = () => {
        setFormData(initialFormData);
        setFormErrors({});
        setEditId(null);
        setPinLookupLoading(false);
    };

    const openAddPopup = () => {
        if (!canAddBranch) {
            showNotification('You are not authorized to add insurer branches.', 'error');
            return;
        }

        resetForm();
        setShowPopup(true);
    };

    const closePopup = () => {
        setShowPopup(false);
        resetForm();
    };

    const validateForm = () => {
        const errors = {};
        const insurerName = insurers.find((item) => item._id === formData.insurerId)?.insurerName;

        if (!formData.insurerId || !insurerName) {
            errors.insurerId = 'Please select an insurer.';
        }
        if (!formData.branchName.trim()) {
            errors.branchName = 'Branch Name is required.';
        }
        if (!formData.contactPersonName.trim()) {
            errors.contactPersonName = 'Contact Person Name is required.';
        }
        if (!formData.contactNumber.trim()) {
            errors.contactNumber = 'Contact Number is required.';
        } else {
            const digitsOnly = formData.contactNumber.replace(/\D/g, '');
            if (!contactNumberRegex.test(formData.contactNumber) || digitsOnly.length < 6 || digitsOnly.length > 12) {
                errors.contactNumber = 'Enter a valid mobile or landline number.';
            }
        }
        if (!formData.email.trim()) {
            errors.email = 'Email is required.';
        } else if (!emailRegex.test(formData.email.trim())) {
            errors.email = 'Enter a valid email address.';
        }
        if (!formData.address.trim()) {
            errors.address = 'Address is required.';
        }
        if (!formData.pinCode.trim()) {
            errors.pinCode = 'Pin Code is required.';
        } else if (!pinCodeRegex.test(formData.pinCode.trim())) {
            errors.pinCode = 'Pin Code must be 6 digits.';
        }
        if (!formData.city.trim()) {
            errors.city = 'City is required.';
        }
        if (!formData.state.trim()) {
            errors.state = 'State is required.';
        }

        return errors;
    };

    const handleInputChange = (event) => {
        const { name, value } = event.target;
        const sanitizedValue = name === 'pinCode'
            ? value.replace(/\D/g, '').slice(0, 6)
            : name === 'contactNumber'
                ? value.replace(/[^0-9+()\-\s]/g, '').slice(0, 15)
                : value;

        setFormData((prev) => ({
            ...prev,
            [name]: sanitizedValue
        }));

        setFormErrors((prev) => ({
            ...prev,
            [name]: ''
        }));

        if (name === 'pinCode' && sanitizedValue.length < 6) {
            setFormData((prev) => ({
                ...prev,
                city: '',
                state: ''
            }));
        }
    };

    const fetchLocationByPinCode = async (pinCode) => {
        if (!pinCodeRegex.test(pinCode)) {
            return;
        }

        setPinLookupLoading(true);
        try {
            const response = await api.get(`${API_BASE_URL}/api/insurer-branches/pincode/${pinCode}`);
            setFormData((prev) => ({
                ...prev,
                city: response.data.city || '',
                state: response.data.state || ''
            }));
            setFormErrors((prev) => ({
                ...prev,
                pinCode: '',
                city: '',
                state: ''
            }));
        } catch (error) {
            setFormData((prev) => ({
                ...prev,
                city: '',
                state: ''
            }));
            setFormErrors((prev) => ({
                ...prev,
                pinCode: error?.response?.data?.message || 'Unable to fetch City and State for this Pin Code.'
            }));
        } finally {
            setPinLookupLoading(false);
        }
    };

    const handlePinCodeBlur = () => {
        if (formData.pinCode.length === 6) {
            fetchLocationByPinCode(formData.pinCode);
        }
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        if ((!editId && !canAddBranch) || (editId && !canEditBranch)) {
            showNotification(
                editId
                    ? 'You are not authorized to edit insurer branches.'
                    : 'You are not authorized to add insurer branches.',
                'error'
            );
            return;
        }

        const errors = validateForm();
        if (Object.keys(errors).length > 0) {
            setFormErrors(errors);
            return;
        }

        const payload = {
            ...formData,
            branchName: formData.branchName.trim(),
            contactPersonName: formData.contactPersonName.trim(),
            contactNumber: formData.contactNumber.trim(),
            email: formData.email.trim(),
            address: formData.address.trim(),
            city: formData.city.trim(),
            state: formData.state.trim()
        };

        setSaving(true);
        try {
            if (editId) {
                const response = await api.put(`${API_BASE_URL}/api/insurer-branches/${editId}`, payload);
                setBranches((prev) =>
                    prev.map((item) => (item._id === editId ? response.data.branch : item))
                );
                showNotification('Insurer branch updated successfully.', 'success');
            } else {
                const response = await api.post(`${API_BASE_URL}/api/insurer-branches`, payload);
                setBranches((prev) => [response.data.branch, ...prev]);
                showNotification('Insurer branch added successfully.', 'success');
            }

            closePopup();
        } catch (error) {
            const responseErrors = error?.response?.data?.errors || {};
            if (Object.keys(responseErrors).length > 0) {
                setFormErrors(responseErrors);
            }
            showNotification(error?.response?.data?.message || 'Error saving insurer branch.', 'error');
        } finally {
            setSaving(false);
        }
    };

    const handleEdit = (branch) => {
        if (!canEditBranch) {
            showNotification('You are not authorized to edit insurer branches.', 'error');
            return;
        }

        setEditId(branch._id);
        setFormData({
            insurerId: branch.insurerId,
            branchName: branch.branchName || '',
            contactPersonName: branch.contactPersonName || '',
            contactNumber: branch.contactNumber || '',
            email: branch.email || '',
            address: branch.address || '',
            pinCode: branch.pinCode || '',
            city: branch.city || '',
            state: branch.state || ''
        });
        setFormErrors({});
        setShowPopup(true);
    };

    const askDeleteBranch = (branch) => {
        if (!canDeleteBranch) {
            showNotification('You are not authorized to delete insurer branches.', 'error');
            return;
        }

        setBranchToDelete(branch);
        setShowDeleteConfirm(true);
    };

    const cancelDeleteBranch = () => {
        setBranchToDelete(null);
        setShowDeleteConfirm(false);
    };

    const confirmDeleteBranch = async () => {
        if (!branchToDelete?._id) {
            return;
        }

        if (!canDeleteBranch) {
            showNotification('You are not authorized to delete insurer branches.', 'error');
            return;
        }

        try {
            await api.delete(`${API_BASE_URL}/api/insurer-branches/${branchToDelete._id}`);
            setBranches((prev) => prev.filter((item) => item._id !== branchToDelete._id));
            showNotification('Insurer branch deleted successfully.', 'success');
            cancelDeleteBranch();
        } catch (error) {
            showNotification(error?.response?.data?.message || 'Error deleting insurer branch.', 'error');
        }
    };

    const uniqueOptions = (values) =>
        [...new Set(values.map((value) => String(value || '').trim()).filter(Boolean))]
            .sort((a, b) => a.localeCompare(b));

    const filteredBranches = useMemo(() => {
        const query = filters.search.trim().toLowerCase();
        return branches.filter((branch) => {
            const searchText = [
                branch.insurerName,
                branch.branchName,
                branch.contactPersonName,
                branch.contactNumber,
                branch.email,
                branch.address,
                branch.pinCode,
                branch.city,
                branch.state
            ].join(' ').toLowerCase();

            const matchesSearch = !query || searchText.includes(query);
            const matchesInsurer = !filters.insurerId || branch.insurerId === filters.insurerId;
            const matchesCity = !filters.city || branch.city === filters.city;
            const matchesState = !filters.state || branch.state === filters.state;

            return matchesSearch && matchesInsurer && matchesCity && matchesState;
        });
    }, [branches, filters]);

    const summary = useMemo(() => ({
        total: branches.length,
        filtered: filteredBranches.length,
        insurersCovered: new Set(branches.map((branch) => branch.insurerId).filter(Boolean)).size,
        cities: uniqueOptions(branches.map((branch) => branch.city)).length,
        withEmail: branches.filter((branch) => branch.email).length
    }), [branches, filteredBranches.length]);

    const filterOptions = useMemo(() => ({
        cities: uniqueOptions(branches.map((branch) => branch.city)),
        states: uniqueOptions(branches.map((branch) => branch.state))
    }), [branches]);

    const paginatedBranches = filteredBranches.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    const startItem = filteredBranches.length ? (currentPage - 1) * itemsPerPage + 1 : 0;
    const endItem = Math.min(currentPage * itemsPerPage, filteredBranches.length);

    const handleFilterChange = (field, value) => {
        setFilters((prev) => ({ ...prev, [field]: value }));
        setCurrentPage(1);
    };

    const resetFilters = () => {
        setFilters({
            search: '',
            insurerId: '',
            city: '',
            state: ''
        });
        setCurrentPage(1);
    };

    return (
        <div className="insurer-branch-page">
            <div className="sidebar">
                <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} />
            </div>

            <div className={`insurer-branch-shell ${collapsed ? 'collapsed' : ''}`}>
                <div className="insurer-branch-sticky-header">
                    <Header />
                </div>

                <main className="insurer-branch-scroll-area">
                <section className="insurer-branch-content">
                    <div className="branch-page-hero">
                        <div>
                            <span className="branch-page-eyebrow">System Settings / Product Master</span>
                            <h1>Insurer Branch Master</h1>
                            <p>Maintain insurer branch contact, service location, and communication details from one workspace.</p>
                        </div>
                        <div className="branch-hero-stats">
                            <span>Total Branches</span>
                            <strong>{summary.total}</strong>
                            <small>{summary.filtered} matching current filters</small>
                        </div>
                    </div>

                    <div className="branch-summary-grid">
                        <article className="branch-summary-card is-blue">
                            <Building2 size={22} />
                            <span>Total Branches</span>
                            <strong>{summary.total}</strong>
                            <small>All active branch records</small>
                        </article>
                        <article className="branch-summary-card is-green">
                            <ShieldCheck size={22} />
                            <span>Insurers Covered</span>
                            <strong>{summary.insurersCovered}</strong>
                            <small>Mapped insurer companies</small>
                        </article>
                        <article className="branch-summary-card is-amber">
                            <MapPin size={22} />
                            <span>Service Cities</span>
                            <strong>{summary.cities}</strong>
                            <small>Unique operating cities</small>
                        </article>
                        <article className="branch-summary-card is-teal">
                            <Mail size={22} />
                            <span>Email Available</span>
                            <strong>{summary.withEmail}</strong>
                            <small>Branches with email contact</small>
                        </article>
                    </div>

                    <section className="branch-filter-card">
                        <div className="branch-filter-header">
                            <div>
                                <h2>Search & Filters</h2>
                                <p>Search by insurer, branch, contact person, phone, email, city, state, or pin code.</p>
                            </div>
                            <div className="branch-result-pill">{filteredBranches.length} records</div>
                        </div>

                        <div className="branch-filter-grid">
                            <label className="branch-search-field">
                                <span>Search</span>
                                <div>
                                    <Search size={17} />
                                    <input
                                        type="search"
                                        placeholder="Insurer, branch, contact, city"
                                        value={filters.search}
                                        onChange={(e) => handleFilterChange('search', e.target.value)}
                                    />
                                </div>
                            </label>
                            <label>
                                <span>Insurer</span>
                                <select
                                    value={filters.insurerId}
                                    onChange={(e) => handleFilterChange('insurerId', e.target.value)}
                                >
                                    <option value="">All insurers</option>
                                    {insurers.map((insurer) => (
                                        <option key={insurer._id} value={insurer._id}>
                                            {insurer.insurerName}
                                        </option>
                                    ))}
                                </select>
                            </label>
                            <label>
                                <span>City</span>
                                <select value={filters.city} onChange={(e) => handleFilterChange('city', e.target.value)}>
                                    <option value="">All cities</option>
                                    {filterOptions.cities.map((city) => (
                                        <option key={city} value={city}>{city}</option>
                                    ))}
                                </select>
                            </label>
                            <label>
                                <span>State</span>
                                <select value={filters.state} onChange={(e) => handleFilterChange('state', e.target.value)}>
                                    <option value="">All states</option>
                                    {filterOptions.states.map((state) => (
                                        <option key={state} value={state}>{state}</option>
                                    ))}
                                </select>
                            </label>
                        </div>

                        <div className="branch-action-row">
                            {canAddBranch && (
                                <button className="branch-btn branch-btn-primary" onClick={openAddPopup}>
                                    <Plus size={16} /> Add Branch
                                </button>
                            )}
                            <button className="branch-btn branch-btn-ghost" onClick={resetFilters}>
                                <RefreshCw size={16} /> Reset
                            </button>
                        </div>
                    </section>

                    <div className="branch-table-card">
                        <div className="branch-table-toolbar">
                            <div>
                                <h2>Branch Records</h2>
                                <p>Sticky insurer column with contact and location details.</p>
                            </div>
                        </div>

                        {loading ? (
                            <div className="branch-loader">Loading insurer branches...</div>
                        ) : (
                            <div className="branch-table-wrap">
                                <table className="branch-table">
                                    <thead>
                                        <tr>
                                            <th className="branch-sticky-name">Insurer</th>
                                            <th>Branch Name</th>
                                            <th>Contact Person</th>
                                            <th>Contact Number</th>
                                            <th>Email</th>
                                            <th>City</th>
                                            <th>State</th>
                                            <th>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {paginatedBranches.length > 0 ? (
                                            paginatedBranches.map((branch) => (
                                                <tr key={branch._id}>
                                                    <td className="branch-sticky-name" data-label="Insurer">
                                                        <div className="branch-insurer-cell">
                                                            <span><Building2 size={17} /></span>
                                                            <strong>{branch.insurerName}</strong>
                                                        </div>
                                                    </td>
                                                    <td data-label="Branch Name">{branch.branchName}</td>
                                                    <td data-label="Contact Person">
                                                        <div className="branch-contact-person">
                                                            <Users size={15} /> {branch.contactPersonName}
                                                        </div>
                                                    </td>
                                                    <td data-label="Contact Number">
                                                        <a className="branch-contact-link" href={`tel:${branch.contactNumber}`}>
                                                            <Phone size={15} /> {branch.contactNumber}
                                                        </a>
                                                    </td>
                                                    <td data-label="Email">
                                                        <a className="branch-contact-link" href={`mailto:${branch.email}`}>
                                                            <Mail size={15} /> {branch.email}
                                                        </a>
                                                    </td>
                                                    <td data-label="City">{branch.city}</td>
                                                    <td data-label="State">{branch.state}</td>
                                                    <td data-label="Actions">
                                                        <div className="branch-action-icons">
                                                            {canEditBranch && (
                                                                <button
                                                                    className="icon-btn edit-btn"
                                                                    onClick={() => handleEdit(branch)}
                                                                    title="Edit Branch"
                                                                    aria-label={`Edit ${branch.branchName}`}
                                                                >
                                                                    <Edit size={18} />
                                                                </button>
                                                            )}
                                                            {canDeleteBranch && (
                                                                <button
                                                                    className="icon-btn delete-btn"
                                                                    onClick={() => askDeleteBranch(branch)}
                                                                    title="Delete Branch"
                                                                    aria-label={`Delete ${branch.branchName}`}
                                                                >
                                                                    <Trash2 size={18} />
                                                                </button>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : (
                                            <tr>
                                                <td colSpan="8">
                                                    <div className="branch-empty-state">
                                                        <Building2 size={42} />
                                                        <h3>No Branches Found</h3>
                                                        <p>Try changing the filters or add a new insurer branch.</p>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>

                    {filteredBranches.length > itemsPerPage && (
                        <div className="branch-pagination-row">
                            <div className="branch-pagination-meta">
                                <span>Showing {startItem}-{endItem} of {filteredBranches.length}</span>
                                <small>Filtered branch records</small>
                            </div>
                            <div className="branch-pagination-controls">
                                <Pagination
                                    totalItems={filteredBranches.length}
                                    itemsPerPage={itemsPerPage}
                                    currentPage={currentPage}
                                    onPageChange={setCurrentPage}
                                />
                            </div>
                            <div className="branch-items-per-page">
                                <label>Items per page:</label>
                                <select
                                    value={itemsPerPage}
                                    onChange={(e) => {
                                        setItemsPerPage(Number(e.target.value));
                                        setCurrentPage(1);
                                    }}
                                >
                                    <option value={5}>5</option>
                                    <option value={10}>10</option>
                                    <option value={20}>20</option>
                                    <option value={50}>50</option>
                                </select>
                            </div>
                        </div>
                    )}
                </section>
                </main>
            </div>

            {showPopup && (
                <div className="branch-popup-overlay">
                    <div className="branch-popup">
                        <button className="branch-popup-close" onClick={closePopup}>×</button>
                        <h3>{editId ? 'Edit Insurer Branch' : 'Add Insurer Branch'}</h3>

                        <form className="branch-form" onSubmit={handleSubmit}>
                            <div className="form-group full-width">
                                <label htmlFor="insurerId">Insurer</label>
                                <select
                                    id="insurerId"
                                    name="insurerId"
                                    value={formData.insurerId}
                                    onChange={handleInputChange}
                                >
                                    <option value="">Select Insurer</option>
                                    {insurers.map((insurer) => (
                                        <option key={insurer._id} value={insurer._id}>
                                            {insurer.insurerName}
                                        </option>
                                    ))}
                                </select>
                                {formErrors.insurerId && <span className="field-error">{formErrors.insurerId}</span>}
                            </div>

                            <div className="form-group">
                                <label htmlFor="branchName">Branch Name</label>
                                <input
                                    id="branchName"
                                    type="text"
                                    name="branchName"
                                    value={formData.branchName}
                                    onChange={handleInputChange}
                                />
                                {formErrors.branchName && <span className="field-error">{formErrors.branchName}</span>}
                            </div>

                            <div className="form-group">
                                <label htmlFor="contactPersonName">Contact Person Name</label>
                                <input
                                    id="contactPersonName"
                                    type="text"
                                    name="contactPersonName"
                                    value={formData.contactPersonName}
                                    onChange={handleInputChange}
                                />
                                {formErrors.contactPersonName && <span className="field-error">{formErrors.contactPersonName}</span>}
                            </div>

                            <div className="form-group">
                                <label htmlFor="contactNumber">Contact Number</label>
                                <input
                                    id="contactNumber"
                                    type="text"
                                    name="contactNumber"
                                    value={formData.contactNumber}
                                    onChange={handleInputChange}
                                    placeholder="Mobile or landline number"
                                />
                                {formErrors.contactNumber && <span className="field-error">{formErrors.contactNumber}</span>}
                            </div>

                            <div className="form-group">
                                <label htmlFor="email">Email</label>
                                <input
                                    id="email"
                                    type="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleInputChange}
                                />
                                {formErrors.email && <span className="field-error">{formErrors.email}</span>}
                            </div>

                            <div className="form-group full-width">
                                <label htmlFor="address">Address</label>
                                <textarea
                                    id="address"
                                    name="address"
                                    value={formData.address}
                                    onChange={handleInputChange}
                                    rows="3"
                                />
                                {formErrors.address && <span className="field-error">{formErrors.address}</span>}
                            </div>

                            <div className="form-group">
                                <label htmlFor="pinCode">Pin Code</label>
                                <input
                                    id="pinCode"
                                    type="text"
                                    name="pinCode"
                                    value={formData.pinCode}
                                    onChange={handleInputChange}
                                    onBlur={handlePinCodeBlur}
                                    placeholder="6-digit Pin Code"
                                />
                                {pinLookupLoading && <span className="field-hint">Fetching city and state...</span>}
                                {formErrors.pinCode && <span className="field-error">{formErrors.pinCode}</span>}
                            </div>

                            <div className="form-group">
                                <label htmlFor="city">City</label>
                                <input
                                    id="city"
                                    type="text"
                                    name="city"
                                    value={formData.city}
                                    onChange={handleInputChange}
                                    readOnly
                                />
                                {formErrors.city && <span className="field-error">{formErrors.city}</span>}
                            </div>

                            <div className="form-group">
                                <label htmlFor="state">State</label>
                                <input
                                    id="state"
                                    type="text"
                                    name="state"
                                    value={formData.state}
                                    onChange={handleInputChange}
                                    readOnly
                                />
                                {formErrors.state && <span className="field-error">{formErrors.state}</span>}
                            </div>

                            <div className="form-actions full-width">
                                <button type="button" className="secondary-btn" onClick={closePopup}>
                                    Cancel
                                </button>
                                <button type="submit" className="primary-btn" disabled={saving}>
                                    {saving ? 'Saving...' : editId ? 'Update Branch' : 'Save Branch'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {showDeleteConfirm && (
                <div className="branch-popup-overlay">
                    <div className="branch-popup branch-confirm-popup">
                        <button className="branch-popup-close" onClick={cancelDeleteBranch}>x</button>
                        <h3>Delete Insurer Branch</h3>
                        <p className="branch-confirm-text">
                            Are you sure you want to delete
                            {' '}
                            <strong>{branchToDelete?.branchName}</strong>
                            {' '}
                            for
                            {' '}
                            <strong>{branchToDelete?.insurerName}</strong>
                            ?
                        </p>
                        <div className="form-actions full-width">
                            <button type="button" className="secondary-btn" onClick={cancelDeleteBranch}>
                                Cancel
                            </button>
                            <button type="button" className="danger-btn" onClick={confirmDeleteBranch}>
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
};

export default InsurerBranchMaster;
